/**
 * HomeBlogSection — surface the 3 latest published blog posts on
 * the public homepage.
 *
 * Backend: GET /api/blog?limit=3 (already exposes the public list
 * with newest-first ordering).
 *
 * UX:
 *   - Hidden if the API returns nothing (avoids an empty section
 *     during the very first weeks of the blog).
 *   - Image / extract / "Lire" CTA per card.
 *   - "Voir tous les articles" CTA at the bottom -> /blog.
 *
 * SEO: each card link is a real <Link to="/blog/:slug">. The
 * existing /blog list and /blog/:slug pages are public + SEO-
 * indexable, so crawlers reach the full blog from the homepage.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
// INSTANCE PARTAGÉE (audit 06/07/2026) : retry réseau + en-têtes
// X-Client/X-App-Version, comme tout appel backend. Cf. DUPLICATE_GUARDRAILS.
import { api } from "../context/AuthContext";

// 2 articles sur mobile (Lucas, 23/10/2026 : épurer l'accueil) ; le blog complet reste à un clic.
const SHOWN = 4;
// Une rangée pleine à chaque largeur (Lucas, 27/10/2026 : « en version
// ordinateur le blog n'est pas complet ») : 2 articles sur mobile, 3 sur
// tablette, 4 sur ordinateur — autant que de colonnes de la grille.
const visibilite = (i) => (i === 2 ? "hidden sm:block" : i === 3 ? "hidden lg:block" : "block");

function _excerpt(post, maxLen = 140) {
  // Use meta_description if the editor wrote one; else strip HTML
  // from content and take the first ~140 chars.
  const raw =
    post.excerpt ||
    post.meta_description ||
    (post.content ? String(post.content).replace(/<[^>]+>/g, " ") : "") ||
    "";
  const clean = raw.replace(/\s+/g, " ").trim();
  if (clean.length <= maxLen) return clean;
  return clean.slice(0, maxLen).trimEnd() + "…";
}

function _formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function HomeBlogSection() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/blog", { params: { limit: SHOWN, offset: 0 } })
      .then((r) => {
        if (!alive) return;
        setPosts((r.data?.posts || []).slice(0, SHOWN));
      })
      .catch(() => {
        if (alive) setPosts([]);
      });
    return () => { alive = false; };
  }, []);

  if (posts === null) {
    // Loading — render a small skeleton so the homepage doesn't jump.
    return (
      <div className="container mx-auto px-4 py-10 max-w-5xl">
        <div className="h-6 w-40 bg-slate-100 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`${visibilite(i)} rounded-xl bg-slate-100 animate-pulse h-40`} />
          ))}
        </div>
      </div>
    );
  }
  if (!posts.length) return null;

  return (
    <section
      className="container mx-auto px-4 py-10 max-w-5xl"
      data-testid="home-blog-section"
      aria-labelledby="home-blog-heading"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 id="home-blog-heading" className="text-xl font-bold text-slate-900">
            Le blog Gively
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Conseils, retours d'associations et coulisses de la marketplace solidaire.
          </p>
        </div>
        <Link
          to="/blog"
          className="text-sm text-[#1DA1F2] font-semibold hover:underline whitespace-nowrap"
        >
          Voir tous les articles →
        </Link>
      </div>

      {/* Compact (retour Lucas) : grille 2/3/4 colonnes, petite vignette, pas
          de gros pavé d'extrait — titre + date + « Lire ». */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {posts.map((p, i) => {
          const slug = p.slug || p.post_id;
          const cover = p.cover_image || p.image_url || null;
          const date = _formatDate(p.published_date || p.created_at);
          return (
            <Link
              key={slug}
              to={`/blog/${slug}`}
              className={`group ${visibilite(i)} bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-slate-100`}
            >
              {/* Cover — clean gradient when missing, never a stock image. */}
              <div className="h-24 bg-gradient-to-br from-blue-100 to-violet-100 overflow-hidden">
                {cover && (
                  <img
                    src={cover}
                    alt={p.title ? `Illustration : ${p.title}` : "Article du blog"}
                    loading="lazy"
                    decoding="async"
                    width="300"
                    height="96"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                )}
              </div>
              <div className="p-3">
                {date && (
                  <p className="text-[10px] text-slate-400 font-medium mb-0.5">{date}</p>
                )}
                <h3 className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">
                  {p.title}
                </h3>
                <p className="mt-1.5 text-xs font-semibold text-[#1DA1F2] group-hover:underline">
                  Lire →
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
